"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Effect } from "effect";
import { RpcClient } from "@effect/rpc";
import type { Rpc } from "@effect/rpc";
import { cookieConsentEq, CookieConsentRpc } from "@/lib/effect-query";
import { UpdateCookieConsentRpc, BulkUpdateCookieConsentRpc } from "@/presentation/rpc/cookie-consent-rpc";

/**
 * Input type for updating cookie consent.
 * Derived from RPC contract which uses CookieConsentPartialUpdateSchema from domain.
 * This automatically excludes protected fields (userId, ipCountry, userAgent).
 */
type UpdateCookieConsentInput = Rpc.Payload<typeof UpdateCookieConsentRpc>;

/**
 * Input type for bulk consent updates (accept all / reject all).
 * Derived from RPC contract.
 */
type BulkConsentInput = Rpc.Payload<typeof BulkUpdateCookieConsentRpc>;

// =============================================================================
// Query Keys
// =============================================================================

/**
 * Query keys factory for cookie consent queries
 */
export const cookieConsentKeys = {
  all: ["cookie-consent"] as const,
  current: () => [...cookieConsentKeys.all, "current"] as const
};

// =============================================================================
// Query Options
// =============================================================================

/**
 * Query options for fetching current user's cookie consent
 * @param enabled - Whether the query should be enabled (default: true)
 */
const cookieConsentQueryOptions = (enabled: boolean = true) => cookieConsentEq.queryOptions({
  queryKey: cookieConsentKeys.current(),
  queryFn: () => Effect.gen(function* () {
    const rpcClient = yield* RpcClient.make(CookieConsentRpc);
    return yield* rpcClient.getCookieConsent();
  }).pipe(Effect.scoped),
  staleTime: 5 * 60 * 1000,
  // 5 minutes
  gcTime: 10 * 60 * 1000,
  // 10 minutes
  enabled
});

// =============================================================================
// Query Hook
// =============================================================================

/**
 * Hook to fetch current user's cookie consent from backend
 *
 * Returns the consent record, creating one with GDPR defaults if none exists.
 *
 * @param options.enabled - Whether to enable the query (default: true). Set to false to prevent
 *                          the query from running (e.g., when user is not authenticated)
 *
 * @example
 * ```tsx
 * const { consent, isLoading, error, refetch } = useCookieConsentRpc({ enabled: isAuthenticated })
 *
 * if (isLoading) return <Spinner />
 * if (error) return <Error message={error.message} />
 *
 * return <div>Analytics: {consent.analytics ? 'Yes' : 'No'}</div>
 * ```
 */
export function useCookieConsentRpc(options) {
  const $ = _c(8);
  const enabled = options?.enabled ?? true;
  let t0;
  if ($[0] !== enabled) {
    t0 = cookieConsentQueryOptions(enabled);
    $[0] = enabled;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    data,
    isLoading,
    error,
    refetch,
    isSuccess
  } = useQuery(t0);
  let t1;
  if ($[2] !== data || $[3] !== error || $[4] !== isLoading || $[5] !== isSuccess || $[6] !== refetch) {
    t1 = {
      consent: data,
      isLoading,
      isSuccess,
      error,
      refetch
    };
    $[2] = data;
    $[3] = error;
    $[4] = isLoading;
    $[5] = isSuccess;
    $[6] = refetch;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Hook to update specific cookie consent categories
 *
 * Allows partial updates - only provided fields are modified.
 *
 * @example
 * ```tsx
 * const { updateConsent, isUpdating } = useUpdateCookieConsentRpc()
 *
 * const handleToggle = (checked: boolean) => {
 *   updateConsent({ analytics: checked, consentMethod: 'settings' })
 * }
 * ```
 */
export function useUpdateCookieConsentRpc() {
  const queryClient = useQueryClient();
  const mutation = useMutation(cookieConsentEq.mutationOptions({
    mutationKey: ["cookie-consent", "update"],
    mutationFn: (input: UpdateCookieConsentInput) => Effect.gen(function* () {
      const rpcClient = yield* RpcClient.make(CookieConsentRpc);
      return yield* rpcClient.updateCookieConsent(input);
    }).pipe(Effect.scoped),
    onSuccess: () => {
      // Invalidate the query to refetch
      queryClient.invalidateQueries({
        queryKey: cookieConsentKeys.all
      });
    }
  }));
  return {
    /** Trigger the mutation (fire-and-forget) */
    updateConsent: mutation.mutate,
    /** Trigger the mutation (awaitable) */
    updateConsentAsync: mutation.mutateAsync,
    /** Whether the mutation is in progress */
    isUpdating: mutation.isPending,
    /** Error if mutation failed */
    error: mutation.error
  };
}

/**
 * Hook for bulk consent updates (accept all / reject all)
 *
 * Convenience method for common banner interactions.
 *
 * @example
 * ```tsx
 * const { bulkUpdate, isUpdating } = useBulkUpdateCookieConsentRpc()
 *
 * const handleAcceptAll = () => {
 *   bulkUpdate({ acceptAll: true, consentMethod: 'banner' })
 * }
 *
 * const handleRejectAll = () => {
 *   bulkUpdate({ acceptAll: false, consentMethod: 'banner' })
 * }
 * ```
 */
export function useBulkUpdateCookieConsentRpc() {
  const queryClient = useQueryClient();
  const mutation = useMutation(cookieConsentEq.mutationOptions({
    mutationKey: ["cookie-consent", "bulk-update"],
    mutationFn: (input: BulkConsentInput) => Effect.gen(function* () {
      const rpcClient = yield* RpcClient.make(CookieConsentRpc);
      return yield* rpcClient.bulkUpdateCookieConsent(input);
    }).pipe(Effect.scoped),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cookieConsentKeys.all
      });
    }
  }));
  return {
    /** Trigger the bulk mutation (fire-and-forget) */
    bulkUpdate: mutation.mutate,
    /** Trigger the bulk mutation (awaitable) */
    bulkUpdateAsync: mutation.mutateAsync,
    /** Whether the mutation is in progress */
    isUpdating: mutation.isPending,
    /** Error if mutation failed */
    error: mutation.error
  };
}

/**
 * Hook to delete the current user's cookie consent on the backend.
 *
 * Returns the user to a true pending state — the next `getCookieConsent`
 * call will return null and the consent banner will re-surface. Used by
 * the "Reset Consent" affordance on /settings/privacy.
 */
export function useDeleteCookieConsentRpc() {
  const queryClient = useQueryClient();
  const mutation = useMutation(cookieConsentEq.mutationOptions({
    mutationKey: ["cookie-consent", "delete"],
    mutationFn: () => Effect.gen(function* () {
      const rpcClient = yield* RpcClient.make(CookieConsentRpc);
      return yield* rpcClient.deleteCookieConsent();
    }).pipe(Effect.scoped),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cookieConsentKeys.all
      });
    }
  }));
  return {
    deleteConsent: mutation.mutate,
    deleteConsentAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}